// src/utils/autoScroller.js - Use OSMD's actual system data

export class AutoScroller {
    constructor(osmdContainer, osmd) {
      this.container = osmdContainer;
      this.osmd = osmd;
      this.highestSystemSeen = -1;
      this.systems = []; // Cache of actual systems from OSMD
      this.lookaheadBeats = 0; // Number of beats to look ahead
      this.jumpNext = false; // First move after a fresh start jumps instead of gliding
    }

    // Set the lookahead beats for scroll timing
    setLookaheadBeats(beats) {
      const oldValue = this.lookaheadBeats;
      this.lookaheadBeats = beats || 0;
      // console.log('🎯 AutoScroller.setLookaheadBeats: changed from', oldValue, 'to', this.lookaheadBeats, 'beats');
    }
  
    // Extract the systems (rows of measures) from OSMD, with their top edge
    // in the container's scroll coordinates
    extractSystemsFromOSMD() {
      if (!this.osmd?.graphic?.measureList || !this.container) {
        return [];
      }

      try {
        const systems = [];
        // OSMD positions are in units of 10px at zoom 1, measured from its SVG
        const scale = 10 * (this.osmd.zoom || 1);
        const svg = this.container.querySelector('svg[id^="osmdSvgPage"]');
        const svgTop = svg
          ? svg.getBoundingClientRect().top - this.container.getBoundingClientRect().top + this.container.scrollTop
          : 0;

        // OSMD stores measures in a 2D array: measureList[measureIndex][staffIndex];
        // the measures of one system share their top edge
        for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
          const measureRow = this.osmd.graphic.measureList[measureIndex];
          const firstStaff = measureRow?.find(measure => measure?.boundingBox);
          if (!firstStaff) continue;
          const systemY = svgTop + firstStaff.boundingBox.absolutePosition.y * scale;
          const previous = systems[systems.length - 1];
          if (previous && Math.abs(systemY - previous.systemY) < 1) continue;
          systems.push({
            measureIndex: measureIndex,
            systemY: systemY,
            systemNumber: systems.length
          });
        }

        return systems;
      } catch (error) {
        return [];
      }
    }
  
    // Find which actual system contains highlighted notes (red current or yellow lookahead)
    findSystemForHighlightedNotes() {
      if (!this.container) return null;
  
      try {
        // Look for lookahead notes first if lookahead is enabled, otherwise look for red notes
        let targetElements;
        if (this.lookaheadBeats > 0) {
          // Look for near-black lookahead notes (invisible to user, detectable by autoscroll)
          targetElements = this.container.querySelectorAll('*[style*="fill: rgb(1, 1, 1)"], *[style*="stroke: rgb(1, 1, 1)"]');
        }
        
        // If no lookahead notes found or lookahead is disabled, fall back to red notes
        if (!targetElements || targetElements.length === 0) {
          targetElements = this.container.querySelectorAll('*[style*="fill: rgb(255, 0, 0)"], *[style*="fill:#FF0000"], *[style*="fill: #FF0000"]');
        }
        
        if (targetElements.length === 0) return null;
  
        // Get Y position of first target note
        const firstTargetElement = targetElements[0];
        const elementRect = firstTargetElement.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        const noteY = elementRect.top - containerRect.top + this.container.scrollTop;
  
        // Re-read every time: a zoom or resize re-lays the systems
        this.systems = this.extractSystemsFromOSMD();
        if (this.systems.length === 0) return null;

        // Find the system that contains this Y position. A note above the
        // first staff (a high note, a stem) still belongs to the first system.
        for (let i = 0; i < this.systems.length; i++) {
          const nextSystem = this.systems[i + 1];
          const systemBottom = nextSystem ? nextSystem.systemY : Infinity;
          if (noteY < systemBottom) {
            return i;
          }
        }

        return null;
      } catch (error) {
        return null;
      }
    }
  
    // Center on actual system
    centerOnSystem(systemNumber) {
      if (!this.container || systemNumber >= this.systems.length) return;
  
      const system = this.systems[systemNumber];
      const nextSystem = this.systems[systemNumber + 1];
      
      const systemTop = system.systemY;
      const systemBottom = nextSystem ? nextSystem.systemY : systemTop + 100;
      const systemCenter = (systemTop + systemBottom) / 2;
      
      const containerHeight = this.container.clientHeight;
      const targetScrollTop = Math.max(0, systemCenter - (containerHeight / 2));

      if (this.jumpNext) {
        this.jumpNext = false;
        this.jumpTo(targetScrollTop);
        return;
      }

      this.container.scrollTo({
        left: this.container.scrollLeft,
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }

    // Scroll without animation (the stylesheet makes the container smooth)
    jumpTo(top) {
      const behavior = this.container.style.scrollBehavior;
      this.container.style.scrollBehavior = 'auto';
      this.container.scrollTop = top;
      this.container.style.scrollBehavior = behavior;
    }
  
    update() {
      // console.log('🔍 AutoScroller.update() called - lookaheadBeats:', this.lookaheadBeats);
      
      if (!this.container) {
        // console.log('❌ No container');
        return;
      }
  
      const activeSystem = this.findSystemForHighlightedNotes();
      // console.log('🔍 Active system found:', activeSystem);
      
      if (activeSystem === null) {
        // console.log('❌ No active system found');
        return;
      }
  
      // Always log what we're doing, even without lookahead
      // console.log('🔍 Current state: activeSystem=', activeSystem, 'lookaheadBeats=', this.lookaheadBeats, 'highestSystemSeen=', this.highestSystemSeen);
      
      // The activeSystem now represents the system containing the appropriate notes (yellow lookahead or red current)
      let targetScrollSystem = activeSystem;
  
      // Only scroll if we haven't already scrolled to this target system or beyond
      if (targetScrollSystem > this.highestSystemSeen) {
        // Scroll to the target system
        const systemToScrollTo = Math.min(targetScrollSystem, this.systems.length - 1);
        
        // console.log('🚀 SCROLLING: to system', systemToScrollTo, 'because targetScrollSystem', targetScrollSystem, '> highestSystemSeen', this.highestSystemSeen);
        this.centerOnSystem(systemToScrollTo);
        this.highestSystemSeen = targetScrollSystem; // Track what we intended to scroll to
      } else {
        // console.log('🚫 NOT SCROLLING: targetScrollSystem', targetScrollSystem, '<= highestSystemSeen', this.highestSystemSeen);
      }
    }
  
    scrollToNotes(highlightedNoteIds) {
      this.update();
    }
  
    reset() {
      this.highestSystemSeen = -1;
    }
  
    // A fresh start jumps rather than glides: on iPhone the long smooth scroll
    // back to the top, landing as the practice controls change size, never
    // happened. `startsAtTop`: playback starts at the beginning of the score.
    initialize(startsAtTop = false) {
      this.highestSystemSeen = -1;
      this.jumpNext = true;
      if (startsAtTop && this.container) this.jumpTo(0);
      this.systems = this.extractSystemsFromOSMD(); // Extract systems on init
      this.update();
    }
  }